import { useStore } from '@/state/store';
import { ASSET_CLASS_DEFAULTS, bucketFromAssetClass } from '@/engine';
import { fmtCurrency, fmtRatioPct } from '@/lib/format';
import type { AssetClass, CollateralBucket, LiquidationPolicy } from '@/types';
import { TOOLTIPS } from '@/content/tooltips';
import { SelectField } from './controls';

const ASSET_OPTIONS = (Object.keys(ASSET_CLASS_DEFAULTS) as AssetClass[]).map((k) => ({
  value: k,
  label: ASSET_CLASS_DEFAULTS[k].label,
}));

const POLICY_OPTIONS: { value: LiquidationPolicy; label: string }[] = [
  { value: 'schwab_liquidity', label: 'Schwab liquidity (default)' },
  { value: 'most_tax_efficient', label: 'Most tax-efficient' },
  { value: 'lowest_advance_first', label: 'Lowest advance first' },
  { value: 'preserve_high_advance', label: 'Preserve high advance' },
  { value: 'pro_rata', label: 'Pro-rata' },
];

const cellInput =
  'w-full rounded border border-slate-300 bg-white px-1.5 py-1 text-xs tabular-nums dark:border-slate-600 dark:bg-slate-900';

/** Asset-class bucket editor with derived totals and the liquidation policy (§10.2, §6.21). */
export function BucketEditor() {
  const buckets = useStore((s) => s.inputs.collateralBuckets);
  const policy = useStore((s) => s.inputs.liquidationPolicy);
  const setInputs = useStore((s) => s.setInputs);

  const update = (next: CollateralBucket[]) => setInputs({ collateralBuckets: next });
  const patch = (i: number, p: Partial<CollateralBucket>) =>
    update(buckets.map((b, idx) => (idx === i ? { ...b, ...p } : b)));

  const totalValue = buckets.reduce((s, b) => s + b.value, 0);
  const totalLVC = buckets.reduce((s, b) => s + (b.eligible ? b.value * b.advanceRate : 0), 0);
  const blended = totalValue > 0 ? totalLVC / totalValue : 0;
  const weightedReturn = totalValue > 0 ? buckets.reduce((s, b) => s + b.value * b.expectedReturnAnnualPct, 0) / totalValue : 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="rounded bg-caution-bg px-2 py-1 text-[11px] text-caution">
        Advance rates are illustrative — Schwab assigns real rates per security.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs">
          <thead className="text-slate-500 dark:text-slate-400">
            <tr>
              <th className="pr-2 font-medium">Asset class</th>
              <th className="pr-2 font-medium">Value</th>
              <th className="pr-2 font-medium">Adv.</th>
              <th className="pr-2 font-medium">Ret%</th>
              <th className="pr-2 font-medium">Vol%</th>
              <th className="pr-2 font-medium">Basis</th>
              <th className="pr-2 font-medium">Elig.</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {buckets.map((b, i) => (
              <tr key={b.id}>
                <td className="pr-2 py-0.5">
                  <select
                    className={cellInput}
                    value={b.assetClass}
                    onChange={(e) => {
                      const seeded = bucketFromAssetClass(e.target.value as AssetClass, b.value);
                      patch(i, { assetClass: seeded.assetClass, advanceRate: seeded.advanceRate, expectedReturnAnnualPct: seeded.expectedReturnAnnualPct, volatilityPct: seeded.volatilityPct, label: seeded.label, eligible: seeded.eligible });
                    }}
                  >
                    {ASSET_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </td>
                <td className="pr-2"><input type="number" className={`${cellInput} w-24`} value={b.value} onChange={(e) => patch(i, { value: Number(e.target.value) })} /></td>
                <td className="pr-2"><input type="number" step={0.05} className={`${cellInput} w-16`} value={b.advanceRate} onChange={(e) => patch(i, { advanceRate: Number(e.target.value) })} /></td>
                <td className="pr-2"><input type="number" step={0.5} className={`${cellInput} w-16`} value={b.expectedReturnAnnualPct} onChange={(e) => patch(i, { expectedReturnAnnualPct: Number(e.target.value) })} /></td>
                <td className="pr-2"><input type="number" step={0.5} className={`${cellInput} w-16`} value={b.volatilityPct} onChange={(e) => patch(i, { volatilityPct: Number(e.target.value) })} /></td>
                <td className="pr-2"><input type="number" className={`${cellInput} w-24`} value={b.costBasis} onChange={(e) => patch(i, { costBasis: Number(e.target.value) })} /></td>
                <td className="pr-2 text-center"><input type="checkbox" className="accent-sky-600" checked={b.eligible} onChange={(e) => patch(i, { eligible: e.target.checked })} /></td>
                <td><button type="button" aria-label="Remove bucket" className="text-danger-surface hover:underline" onClick={() => update(buckets.filter((_, idx) => idx !== i))}>✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        className="self-start rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800"
        onClick={() => update([...buckets, bucketFromAssetClass('broad_etf_large_cap', 100_000)])}
      >
        + Add bucket
      </button>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 rounded bg-slate-50 p-2 text-xs dark:bg-slate-900/50 sm:grid-cols-4">
        <div><dt className="text-slate-500">Total value</dt><dd className="font-semibold tabular-nums">{fmtCurrency(totalValue)}</dd></div>
        <div><dt className="text-slate-500">Total LVC</dt><dd className="font-semibold tabular-nums">{fmtCurrency(totalLVC)}</dd></div>
        <div><dt className="text-slate-500">Blended advance</dt><dd className="font-semibold tabular-nums">{fmtRatioPct(blended)}</dd></div>
        <div><dt className="text-slate-500">Weighted return</dt><dd className="font-semibold tabular-nums">{weightedReturn.toFixed(1)}%</dd></div>
      </dl>

      <SelectField label="Liquidation policy" info={TOOLTIPS.liquidationPolicy} value={policy} options={POLICY_OPTIONS} onChange={(v) => setInputs({ liquidationPolicy: v })} />
    </div>
  );
}
